import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

/**
 * Tiny language switcher: toggles between English and Hindi.
 * - Drops into the top nav (compact icon button) or footer (label button).
 * - Persists choice to localStorage via i18next's LanguageDetector.
 *
 * Two variants:
 *   <LanguageSwitcher variant="compact" />  — icon-only, 32×32, for nav
 *   <LanguageSwitcher variant="text" />     — "EN / हिन्दी" pill, for footer
 */
const LanguageSwitcher = ({ variant = 'compact', className = '' }) => {
  const { i18n, t } = useTranslation();
  const current = i18n.resolvedLanguage || i18n.language || 'en';
  const next = current.startsWith('hi') ? 'en' : 'hi';
  const handleToggle = () => i18n.changeLanguage(next);
  const ariaLabel = next === 'hi' ? t('lang.switch_to_hindi') : t('lang.switch_to_english');

  if (variant === 'text') {
    return (
      <button
        type="button"
        onClick={handleToggle}
        data-testid="lang-switcher-text"
        aria-label={ariaLabel}
        className={`inline-flex items-center gap-2 text-xs text-slate-400 hover:text-[#d4af37] transition-colors ${className}`}
      >
        <Globe className="w-3.5 h-3.5" />
        <span className={current === 'en' ? 'text-[#d4af37]' : ''}>EN</span>
        <span className="text-slate-600">/</span>
        <span className={current.startsWith('hi') ? 'text-[#d4af37]' : ''}>हिन्दी</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      data-testid="lang-switcher-compact"
      aria-label={ariaLabel}
      title={ariaLabel}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-300 hover:text-[#d4af37] hover:bg-white/5 transition-colors ${className}`}
    >
      <Globe className="w-4 h-4" />
      <span className="ml-1 text-[10px] font-semibold tracking-wide">
        {current.startsWith('hi') ? 'हि' : 'EN'}
      </span>
    </button>
  );
};

export default LanguageSwitcher;
